import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "@/services/i18n/client";
import { useAuth } from "@/lib/auth/use-auth";
import { getNavigationItems } from "@/lib/auth/roles";
import {
  Menu,
  ChevronDown,
  ChevronRight,
  ScanLine,
  Send,
  MapPin,
  Package,
  Settings,
  FileText,
  Users,
  ShoppingBag,
  CreditCard,
  TrendingUp,
  LifeBuoy,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@heycircle/ui/sheet";
import { Button } from "@heycircle/ui/button";
import { LanguageSwitcher } from "./language-switcher";

const iconMap: Record<string, LucideIcon> = {
  ScanLine,
  Send,
  MapPin,
  Package,
  Settings,
  FileText,
  Users,
  ShoppingBag,
  CreditCard,
  TrendingUp,
};

// Note: The i18next-cli extractor uses static analysis and cannot discover keys
// from dynamic calls like (t as any)(item.label) or from roles.ts where keys are
// stored as data (label: "nav.home"). We use a static navLabels lookup map here
// so the extractor can see each key explicitly.
export function MobileNav() {
  const [open, setOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  const { t } = useTranslation("common");
  const location = useLocation();
  const navItems = getNavigationItems(user?.role?.slug);

  const navLabels: Record<string, string> = {
    "nav.home": t("nav.home"),
    "nav.scanning": t("nav.scanning"),
    "nav.packages": t("nav.packages"),
    "nav.receive": t("nav.receive"),
    "nav.ship": t("nav.ship"),
    "nav.inventory": t("nav.inventory"),
    "nav.inventoryAll": t("nav.inventoryAll"),
    "nav.settings": t("nav.settings"),
    "nav.integrations": t("nav.integrations"),
    "nav.destinations": t("nav.destinations"),
    "nav.scanLocations": t("nav.scanLocations"),
    "nav.externalDestinations": t("nav.externalDestinations"),
    "nav.contracts": t("nav.contracts"),
    "nav.analytics": t("nav.analytics"),
    "nav.adminDashboard": t("nav.adminDashboard"),
    "nav.companies": t("nav.companies"),
    "nav.adminContracts": t("nav.adminContracts"),
    "nav.users": t("nav.users"),
    "nav.billing": t("nav.billing"),
  };

  const toggleExpanded = (href: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(href)) {
        next.delete(href);
      } else {
        next.add(href);
      }
      return next;
    });
  };

  if (!user) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm">
          <Menu className="h-5 w-5" />
          <span className="sr-only">{t("toggleMenu")}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] sm:w-[320px]">
        <SheetHeader>
          <SheetTitle>
            <span className="font-bold text-lg">{t("appName")}</span>
          </SheetTitle>
        </SheetHeader>
        <nav className="flex flex-col gap-1 mt-6">
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.children?.some((child) => location.pathname.startsWith(child.href)) ?? false);
            const isExpanded = expandedItems.has(item.href);
            const hasChildren = item.children && item.children.length > 0;

            const ItemIcon = item.icon ? iconMap[item.icon] : null;
            return (
              <div key={item.href}>
                {hasChildren ? (
                  // Item with children: clicking the row toggles expansion;
                  // the label acts as a group header, not a link itself.
                  <button
                    onClick={() => toggleExpanded(item.href)}
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3 rounded-md text-sm font-medium transition-colors text-left",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {ItemIcon && <ItemIcon className="h-4 w-4 shrink-0" />}
                      {navLabels[item.label] ?? item.label}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    )}
                  >
                    {ItemIcon && <ItemIcon className="h-4 w-4 shrink-0" />}
                    {navLabels[item.label] ?? item.label}
                  </Link>
                )}

                {hasChildren && isExpanded && (
                  <div className="ml-4 mt-1 flex flex-col gap-1 border-l pl-3">
                    {item.children!.map((child) => {
                      const isChildActive =
                        location.pathname === child.href ||
                        location.pathname.startsWith(child.href + "/");
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => setOpen(false)}
                          className={cn(
                            "px-3 py-2 rounded-md text-sm font-medium transition-colors block",
                            isChildActive
                              ? "bg-primary/10 text-primary"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent"
                          )}
                        >
                          {navLabels[child.label] ?? child.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground">
              <LifeBuoy className="h-4 w-4 shrink-0" />
              {t("support.title")}
            </div>
            <Link
              to="/app/support/defect-reporting"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              {t("support.defectReportingLink")}
            </Link>
            <a
              href={`mailto:${t("support.contactEmail")}`}
              className="flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              {t("support.contactUs")}
            </a>
          </div>
          <div className="pt-4 border-t">
            <LanguageSwitcher />
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
